/* eslint-disable no-console */
import Component from "@base/js/component";
import Chips from "@components/chips/chips";
import MultiSelect from "@components/multi-select/multi-select";
import UrlService from "@services/url-service";
import Calendar from "../calendar/calendar";

export default class SearchResultsGroup extends Component {
  // Defines the element selector which will initialize this component
  static selector = ".search-results-group";

  // Defines children that needs to be queried as part of this component
  static children = {
    filterGroups: [".search-filter--search-results .multi-select"], // Queries a single element
    currentFilter: ".search-results-group__current-filter",
    clearFilters: ".search-results-group__current-filter__clear",
    multiselects: [".search-results-group__siderail .multi-select"],
    multiselectCheckboxes: [
      ".search-results-group__siderail .multi-select input",
    ],
    searchFilterSort: ".search-filter__sort-select",
    searchResultsSort: ".search-results__sort-select",
  };

  // Defines sub components that needs to be queried as part of this component
  static members = {
    chips: Chips,
    calendar: Calendar,
  };

  // Will store queried elements and data
  filterGroups = {
    // <filterName>: { items, allCheckbox }
  };

  // Defines event handlers to attach to children
  eventHandlers = {
    clearFilters: {
      click: this.onClearClick,
    },
  };

  // Should store multiselect instances,
  // keyed by filterName
  multiselects = {};

  // Will be called upon component instantiation
  afterInit() {
    this.handleFilterGroups();
    this.handleSort();

    Promise.all(
      this.children.multiselects.map((multiselect) =>
        MultiSelect.getInstanceByElement(multiselect)
      )
    ).then((_multiselects) => {
      _multiselects.forEach((multiselect) => {
        this.multiselects[multiselect.filterName] = multiselect;
        multiselect.onChange(() => {
          this.onFilterChange();
        });
      });

      this.handleChips();
      this.prePopulateFilters();
    });
  }

  prePopulateFilters() {
    const searchParams = UrlService.getSearchParams();

    // Check existing fields in search params
    Object.values(this.multiselects).forEach((multiselect) => {
      Object.values(multiselect.options).forEach((option) => {
        if (option.element.name in searchParams) {
          let willCheck = false;

          // If not array
          if (!Array.isArray(searchParams[option.element.name])) {
            willCheck = searchParams[option.element.name] == option.value; // eslint-disable-line eqeqeq
          }

          // If array
          if (Array.isArray(searchParams[option.element.name])) {
            willCheck = option.value
              .split(",")
              .every((value) =>
                searchParams[option.element.name].includes(value)
              );
          }

          if (willCheck) {
            this.softSelectFilter(option.element.name, option.value);
          }
        }
      });
    });

    // Check for sort select
    Object.entries(searchParams).forEach(([key, value]) => {
      if (key === this.children.searchResultsSort.name) {
        this.children.searchResultsSort.value = value;
      }
      if (key === this.children.searchFilterSort.name) {
        this.children.searchFilterSort.value = value;
      }
    });
  }

  // Selects a filter without emitting change event,
  // Useful for prepopulating filters without redirecting the page
  softSelectFilter(key, value) {
    const matchedMultiSelect = Object.values(this.multiselects).find(
      (multiselect) => value in multiselect.options
    );

    if (matchedMultiSelect) {
      const multiSelectItem = matchedMultiSelect.options[value];

      if (multiSelectItem) {
        // Prepolutate filter
        matchedMultiSelect.softSelect(value);

        const textContent =
          value === "date-specific-date" ? `Date Range` : multiSelectItem.label;

        // Prepopulate chips
        this.members.chips.add({
          value,
          text:
            value === "All"
              ? `All ${matchedMultiSelect.filterLabel}`
              : textContent,
          key,
        });
      }
    }
  }

  handleSort() {
    [this.children.searchFilterSort, this.children.searchResultsSort].forEach(
      (sort) =>
        sort.addEventListener("change", (e) =>
          this.onSortChange(e.currentTarget)
        )
    );
  }

  handleChips() {
    // Use a marker for chips container
    this.chipsMarker = this.children.currentFilter.nextElementSibling;

    // Remove chips container initially
    this.children.currentFilter.parentElement.removeChild(
      this.children.currentFilter
    );

    // Chips event callbacks
    this.members.chips.onRemove(this.onChipRemove.bind(this));
    this.members.chips.onAdd(this.onChipAdd.bind(this));
  }

  handleFilterGroups() {
    this.children.filterGroups.forEach((filterGroup) => {
      const { filterName } = filterGroup.dataset;

      // selectors
      const HEADING = `.multi-select-heading`;
      const ALL_CHECKBOX = `input[value="All"]`;
      const CHECKBOXES = `input[name="${filterName}"]`;

      // nodes
      const filterHeading = filterGroup.querySelector(HEADING);
      const allCheckbox = filterGroup.querySelector(ALL_CHECKBOX);
      const [...filterCheckboxes] = filterGroup.querySelectorAll(CHECKBOXES);

      // Save filterGroup elements and data
      this.filterGroups[filterName] = {
        allCheckbox,
        title: filterHeading.textContent.trim(),
        items: filterCheckboxes,
      };

      // Handle filter checkboxes
      filterCheckboxes.forEach((element) => {
        element.addEventListener("change", () =>
          this.onFilterCheckboxChange(element, filterName)
        );
      });

      // Handle "all"
      if (allCheckbox) {
        allCheckbox.addEventListener("change", () =>
          this.onAllCheckboxChange(allCheckbox, filterName)
        );
      }
    });
  }

  onFilterCheckboxChange(checkboxElement, filterName) {
    const { value, checked } = checkboxElement;
    let { textContent } = checkboxElement.parentElement.nextElementSibling;

    if (value === "date-specific-date") {
      const dateTextContent = checkboxElement
        .closest(".drawer")
        .querySelector(".multi-select-heading-text").textContent;
      textContent = dateTextContent;
    }

    if (checked) {
      this.members.chips.add({
        value,
        text: textContent.trim(),
        filterName,
      });

      // Remove "all" if there is any
      const allValue = `all-${filterName}`;
      const allFilter = this.members.chips.items.find(
        (chip) => chip.value === allValue
      );

      if (allFilter) this.members.chips.remove(filterName, allValue);
    } else {
      this.members.chips.remove(filterName, value);
    }
  }

  onAllCheckboxChange(checkboxElement, filterName) {
    if (filterName in this.filterGroups) {
      const { title } = this.filterGroups[filterName];

      if (checkboxElement.checked) {
        this.members.chips.add({
          value: checkboxElement.value,
          text: `All ${title}`,
          filterName,
        });

        // Remove all selected filters within the filterGroup
        [...this.members.chips.items].forEach((chip) => {
          if (
            chip.filterName === filterName &&
            chip.value !== checkboxElement.value
          ) {
            this.members.chips.remove(chip.key, chip.value);
          }
        });
      } else {
        this.members.chips.remove(filterName, checkboxElement.value);
      }
    }
  }

  onSortChange(target) {
    // Ensure both sorts are the same
    this.children.searchFilterSort.value = target.value;
    this.children.searchResultsSort.value = target.value;

    this.updatePage();
  }

  onChipRemove(chipData) {
    const matchedMultiSelect = Object.values(this.multiselects).find(
      (multiselect) => chipData.value in multiselect.options
    );

    if (matchedMultiSelect) {
      matchedMultiSelect.unselect(chipData.value);
    }

    this.checkSelectedFiltersLength();
  }

  onChipAdd() {
    this.checkSelectedFiltersLength();
  }

  checkSelectedFiltersLength() {
    if (this.members.chips.items.length) {
      this.showSelectedFilters();
    } else {
      this.hideSelectedFilters();
    }
  }

  showSelectedFilters() {
    this.chipsMarker.insertAdjacentElement(
      "beforebegin",
      this.children.currentFilter
    );

    this.children.currentFilter.classList.remove("hidden");
  }

  hideSelectedFilters() {
    this.children.currentFilter.parentElement.removeChild(
      this.children.currentFilter
    );
  }

  getSelectedFilters() {
    // Select checked fields and not "all"
    const selectedCheckboxes = this.children.multiselectCheckboxes.filter(
      (checkbox) => checkbox.checked && !/all/i.test(checkbox.value)
    );

    return selectedCheckboxes.reduce((all, checkbox) => {
      if (!checkbox.value.trim()) {
        return all;
      }

      const values = checkbox.value.split(",");

      if (!(checkbox.name in all)) {
        all[checkbox.name] = [];
      }

      all[checkbox.name].push(...values);

      return all;
    }, {});
  }

  onFilterChange() {
    this.updatePage();
  }

  onClearClick(e) {
    e.preventDefault();

    // Clear chips
    this.members.chips.clear();

    // Clear filters
    Object.values(this.multiselects).forEach((multiselect) => {
      multiselect.clear();
    });

    this.updatePage();
  }

  disableCheckboxes() {
    this.element.classList.add("search-results-group--disabled");

    this.children.multiselectCheckboxes.forEach((checkbox) =>
      checkbox.setAttribute("disabled", true)
    );
  }

  updatePage() {
    const currentParams = UrlService.getSearchParams();
    const selectedFilters = this.getSelectedFilters();
    const sortBy = this.children.searchFilterSort.value;

    // Delete page param to reset pagination
    if ("page" in currentParams) {
      delete currentParams.page;
    }

    // Clear current filters in params first
    Object.values(this.multiselects).forEach((multiselect) => {
      Object.values(multiselect.options).forEach(({ element }) => {
        if (element.name in currentParams) {
          delete currentParams[element.name];
        }
      });
    });

    delete currentParams[this.members.calendar.children.fromRange.name];
    delete currentParams[this.members.calendar.children.toRange.name];

    if ("date_range" in selectedFilters) {
      if (selectedFilters.date_range.includes("date-specific-date")) {
        selectedFilters[this.members.calendar.children.fromRange.name] =
          this.members.calendar.children.fromRange.value.replaceAll("/", "%2F");
        selectedFilters[this.members.calendar.children.toRange.name] =
          this.members.calendar.children.toRange.value.replaceAll("/", "%2F");
      }
    }

    // Rebuild params with updated filters
    const searchParamsString = UrlService.toSearchParamsString({
      ...currentParams,
      ...selectedFilters,
      [this.children.searchFilterSort.name]: sortBy,
    });

    this.disableCheckboxes();

    UrlService.rootWindow.location.href =
      UrlService.pathName + searchParamsString;
  }
}

// Remove this line if this component is not intended
// to initialize on DOM load.
SearchResultsGroup.init();
